import React from 'react';
import { useTranslation } from 'react-i18next';
import RouteProgressStep from './RouteProgressStep';

// "attention" is the third state the details-page banner needs: the step's thing
// exists but isn't configured yet (a destination attached with required fields
// still empty). The wizard only ever uses the first three.
export type StepStatus = 'completed' | 'in-progress' | 'upcoming' | 'attention';

type RouteProgressIndicatorProps = {
  stepZeroStatus: StepStatus;
  stepOneStatus: StepStatus;
  stepTwoStatus: StepStatus;
  webhookStepStatus?: StepStatus;
  // How many accounts and flows still have required fields to fill in. Setup can
  // be structurally complete while the Route cannot move data yet.
  pendingSetupCount?: number;
};

// The Quick Start exists only to remind the user what's left: the page hides it
// once nothing is pending, so it carries no "Confirmation" step and no success
// message — a fully configured Route simply has no banner.
const RouteProgressIndicator = ({
  stepZeroStatus,
  stepOneStatus,
  stepTwoStatus,
  webhookStepStatus,
  pendingSetupCount = 0,
}: RouteProgressIndicatorProps) => {
  const { t } = useTranslation();

  return (
    /* A white card, same fill/border/radius as the Route Name and Flow map
           panels below it, so the page reads as one stack of cards on the tint
           rather than a grey block followed by white ones. */
    <div className="flex flex-col items-center rounded-lg border border-teal-t30 bg-white py-4 transition-colors duration-300">
      <div className="flex flex-col">
        <label className="mt-3 text-2xl text-ui-text-primary-text transition-colors duration-300">
          {t('Route Quick Start')}
        </label>
        {/* No fill of its own: the steps are white pills and the card
                    behind them is white, so a tint here only showed up as
                    coloured gaps between the pills. */}
        <div className="my-2 flex h-14 w-max items-center gap-1 rounded-lg p-4">
          <RouteProgressStep
            stepLabel={t('Select Provider')}
            status={stepZeroStatus}
            finalStep={false}
          />
          <RouteProgressStep
            stepLabel={t('Configure Provider')}
            status={stepOneStatus}
            finalStep={false}
          />
          <RouteProgressStep
            stepLabel={t('Connect Destination')}
            status={stepTwoStatus}
            finalStep={!webhookStepStatus}
          />
          {webhookStepStatus && (
            <RouteProgressStep
              stepLabel={t('Configure Webhook')}
              status={webhookStepStatus}
              finalStep={true}
            />
          )}
        </div>
        {pendingSetupCount > 0 && (
          <p className="mb-3 text-sm text-ui-text-primary-text">
            {t(
              'Some required configurations are missing. Look for the exclamation marks on the flow map below.',
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default RouteProgressIndicator;
